/**
 * ProblemCardsV2 — v2 API 만 쓰는 **카드형** 문제 미리보기.
 *
 * 구형 ProblemPreviewCards(707줄)의 v2 판이다. 그 파일은 **손대지 않는다**(오너 지시
 * 2026-08-26) — 대상 밖 세 화면이 그대로 쓰고 있기 때문이다(pages/LearningStatus.js 두 자리,
 * ProblemAgent/WorkDetail.js). 그래서 고치지 않고 **옆에 세운다.**
 *
 * ══ 왜 모달(ProblemPreviewV2)로 대신할 수 없나 ═══════════════════════════════
 * 그쪽은 공용 `Modal` 을 반드시 감싸 반환하는 **창**이다. 여기는 페이지 안에 그대로 쌓이는
 * **목록**이라 창이 아니다. 둘은 얼굴이 아니라 자리가 다르다.
 * 대신 **본문 렌더러는 한 벌을 나눠 쓴다**(ProblemBodyPreview) — 조판이 갈리면 같은 문제가
 * 카드와 창에서 다른 자리에서 줄이 꺾인다.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * ══ 왕복이 줄어든 곳 — 이 부품의 존재 이유 ═══════════════════════════════════
 * 구형은 문제 10개마다 **두 번** 물었다: 본문(v1 `/api/problems/print/page`)과 개념·난이도
 * (v1 `/api/mathcepts/temp/by-prob-ids`). 여기서는 계약 §9 의 스위치 하나로 **한 번**이다:
 *
 *   GET /api/v2/exam-papers/problem-search?problem_ids=…&with_body=1
 *     → item 마다 body_lexical · difficulty · concept_names · preview 가 함께 온다
 *
 * 개념 사전(순번·유형)만 창당 한 번 더 받는데, 그것은 **문제 수와 무관**하고 모듈 캐시(5분)를
 * 같은 사전을 쓰는 다른 화면과 나눠 쓴다. 즉 문제가 몇 개든 사전 왕복은 0~1회다.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * ── 받는 것은 구형과 **글자 하나 안 틀리게 같다** ─────────────────────────────
 * 호출부를 고치지 않고 import 한 줄만 바꿔 갈아끼우기 위해서다. 시그니처가 조금이라도
 * 다르면 옮기는 자리마다 사람이 판단해야 하고, 그 판단이 곧 사고가 된다.
 * (구형이 받던 `problems` 의 두 형태 — 객체형과 sequence 배열형 — 도 그대로 흡수한다.)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bot, Eye, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { fetchProblemItemsV2 } from './problemBodyV2';
import { toBodyHtmlMap } from './lexicalHtml';
import { ConceptBadge, matchConcept, fetchConceptDictV2, conceptPairs } from './conceptBadge';
import ProblemBodyPreview, { LIST_BODY_MAX_SCALE } from './ProblemBodyPreview';
import ProblemNumber from './ProblemNumber';
// 문제 번호 — 두 메뉴가 함께 쓰는 정본 부품(문제 v2 목록 표가 기준 얼굴이다)
import ProblemIdLink from '../ProblemHubV2/ProblemIdLink';
// 난이도 색은 구형 두 벌과 **같은 배열**이다. 주의 — 같은 이름의 상수가 styles/tokens.js 에도
// 있는데 그쪽은 녹색 5단이라 값이 전혀 다르다. 정본은 이 경로다(ProblemPreviewV2 와 같은 선택).
import { DIFFICULTY_COLORS } from '../../pages/LearningStatus/utils/constants';
// AI 실행(LLM 비용)을 쓸 수 있는 계정 유형 — 판정은 이 상수 한 곳에서만 온다
import { AI_RUN_ROLES } from '../../utils/roles';

/** 난이도 색 — 배열이라 1~5 가 인덱스 0~4 다. 척도 밖 값은 색을 주지 않는다 */
const difficultyColor = (d) => DIFFICULTY_COLORS[d - 1] || null;

/** 한 번에 묻는 문항 수 — useRowBodies 와 같은 값을 쓴다(새 숫자를 발명하지 않는다) */
const BATCH = 20;
/** 관찰 이벤트를 모으는 시간(ms). 스크롤 한 번에 열 장이 스치면 열 번 묻지 않고 한 번 묻는다 */
const FLUSH_MS = 120;
/** 화면에 들기 **전에** 미리 받아 둘 거리 — 스크롤이 멈추기 전에 본문이 차 있게 한다 */
const ROOT_MARGIN = '400px 0px';

/**
 * `problems` 흡수 — 구형(ProblemPreviewCards.js:58)과 **같은 규칙**이다.
 *
 * 두 형태가 온다:
 *   ① 객체형   `{ problem_id, step, address }`      — problems_list
 *   ② 배열형   `[pid, difficulty, _, _, _, _, isCorrect]` — sequence
 * ②의 인덱스 1·6 은 문서 없는 매직 넘버다. 구형이 그렇게 읽고 있어 그대로 따른다 —
 * 여기서 다르게 읽으면 같은 데이터가 두 화면에서 다른 난이도를 보인다.
 */
function parseProblems(problems, selectedTest, getProblemResult) {
  return (problems || []).map((p, index) => {
    if (p && p.problem_id != null) {
      return {
        problemId: p.problem_id,
        index,
        isCorrect: getProblemResult ? getProblemResult(selectedTest, p.problem_id) : null,
        difficulty: p.step ?? null,
        address: p.address ?? null,
        raw: p,
      };
    }
    if (Array.isArray(p)) {
      return {
        problemId: p[0],
        index,
        // 배열형은 정오답을 제 안에 갖고 있다. 그래도 getProblemResult 가 있으면 그쪽이 이긴다 —
        // 호출부가 필터·정렬을 거친 뒤 정답 여부를 다시 매길 수 있기 때문이다(구형과 같은 순서).
        isCorrect: getProblemResult ? getProblemResult(selectedTest, p[0]) : (p[6] ?? null),
        difficulty: p[1] ?? null,
        address: null,
        raw: p,
      };
    }
    return { problemId: p, index, isCorrect: null, difficulty: null, address: null, raw: p };
  }).filter((x) => x.problemId != null);
}

function ProblemCardsV2({
  problems = [],
  selectedTest,
  onViewProblem,
  highlightedIndex,
  onHighlight,
  getProblemResult,
  isDesktop = false,
  showConcepts = false,
}) {
  const { getAccessToken, userType } = useAuth();
  /**
   * [AI 변형] 을 그릴지 — **이 부품이 직접 판정한다**(2026-09-10).
   *
   * 이 저장소의 기본은 「부품은 손대지 않고 넘기는 자리에서 가른다」다(roles.js). 그런데 이 버튼은
   * 형제 [문제 상세 보기](onViewProblem)와 달리 **부품 안에서 시작해 부품 안에서 끝난다** —
   * 콜백을 받지 않고 handleMakeVariation 이 스스로 navigate 한다. 콜백으로 바꿔 가르려면 이 부품을
   * 담는 두 화면(StudentsHub/ProblemPreviewPanel · TimepointProblemModal)이 각자 같은 판정을 해야
   * 하고, 그러면 한 곳만 고쳐지는 날이 온다. 그래서 AssignmentPanel 의 배정 삭제와 같은 예외로 둔다.
   *
   * 감추는 이유는 두 겹이다 — ① 변형은 LLM 비용이 나는 실행이다 ② 교사에게는 그 탭 자체가 없어
   * (ProblemHub 의 visibleTabs) 눌러도 [문제 보기] 로 떨어진다. 버튼이 약속한 일과 일어나는 일이
   * 다른 것은 화면이 거짓말을 하는 것이라, 막다른 화면이 아니어도 치우는 편이 맞다.
   */
  const canRunAi = AI_RUN_ROLES.includes(userType);
  const navigate = useNavigate();

  const parsed = useMemo(
    () => parseProblems(problems, selectedTest, getProblemResult),
    [problems, selectedTest, getProblemResult],
  );

  // pid → 검색 item(본문·난이도·개념이 한 응답에 들어 있다). 못 받은 것은 키가 없다.
  const [items, setItems] = useState({});
  // pid → 조판된 본문 HTML. 본문이 없는 문항은 키가 없다(그 카드는 평문 폴백으로 남는다).
  const [htmls, setHtmls] = useState({});
  // 한 번이라도 화면에 든 pid — 이 집합에 든 카드만 본문을 그린다
  const [seen, setSeen] = useState(() => new Set());
  const [dict, setDict] = useState(null);

  const askedRef = useRef(new Set());  // 요청을 보낸 자국(성공·실패 무관)
  const queueRef = useRef(new Set());  // 아직 안 보낸 pid
  const timerRef = useRef(null);
  const busyRef = useRef(false);
  const listRef = useRef(null);

  // StrictMode 는 마운트 → cleanup → 마운트를 돌린다. 마운트마다 세우지 않으면 dev 에서
  // false 로 굳어 응답이 와도 상태를 못 갈고 **영원히 빈 칸**이다(ProblemPreviewV2·useRowBodies
  // 가 같은 주석으로 같은 함정을 막아 두었다).
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  /** 큐에서 한 묶음을 꺼내 받아 온다. 남으면 스스로 다음 묶음을 부른다 */
  const drain = useCallback(async () => {
    if (busyRef.current) return;
    const take = Array.from(queueRef.current).slice(0, BATCH);
    if (take.length === 0) return;
    take.forEach((id) => queueRef.current.delete(id));
    busyRef.current = true;
    try {
      // 자국은 **여기서** 남긴다 — await 뒤로 미루면 그 사이의 렌더가 같은 id 를 다시 큐에 넣는다
      take.forEach((id) => askedRef.current.add(id));
      const map = await fetchProblemItemsV2(take, getAccessToken, { withBody: true });
      if (!aliveRef.current) return;
      // 물어본 id 는 **빠짐없이 자리를 채운다.** 응답에 없는 id(삭제됐거나 status≠999)와 통째로
      // 실패한 묶음을 `null` 로 남기지 않으면 그 카드의 item 이 영영 undefined 라 **스피너가
      // 멈추지 않는다** — 자국(askedRef)이 남아 다시 묻지도 않으므로 영구히 도는 원이 된다.
      // (useRowBodies 가 「키가 안 들어오는 것이 계약」이라 적어 둔 그 규약의 이면이다.)
      const filled = {};
      take.forEach((id) => { filled[String(id)] = map?.[String(id)] ?? null; });
      setItems((prev) => ({ ...prev, ...filled }));
      if (map == null) return;

      // 본문만 뽑아 한 번에 조판한다. 변환기(lexical·katex)를 못 받아도 여기서 던지지 않는다 —
      // 던지면 목록이 통째로 넘어지고, 개념·난이도는 이미 위에서 들어가 있다.
      const docs = {};
      Object.entries(map).forEach(([pid, it]) => {
        if (it?.body_lexical != null) docs[pid] = it.body_lexical;
      });
      if (Object.keys(docs).length === 0) return;
      try {
        const html = await toBodyHtmlMap(docs);
        if (!aliveRef.current || Object.keys(html).length === 0) return;
        setHtmls((prev) => ({ ...prev, ...html }));
      } catch (e) {
        console.error('카드 본문 변환기 로드 실패:', e);
      }
    } catch (e) {
      // 묶음이 통째로 실패했다 — 그 카드들은 평문 폴백으로 남는다. 자국이 남아 있어
      // 스크롤을 오르내려도 같은 요청이 되풀이되지 않는다.
      console.error('카드 본문을 불러오지 못했습니다:', e);
    } finally {
      busyRef.current = false;
      if (aliveRef.current && queueRef.current.size > 0) {
        // 재귀가 아니라 다음 틱으로 넘겨 스택을 쌓지 않는다
        timerRef.current = setTimeout(() => { drain(); }, 0);
      }
    }
  }, [getAccessToken]);

  /** 관찰에 든 pid 를 모았다가 한 번에 처리 */
  const enqueue = useCallback((ids) => {
    let added = false;
    ids.forEach((id) => {
      if (askedRef.current.has(id) || queueRef.current.has(id)) return;
      queueRef.current.add(id);
      added = true;
    });
    setSeen((prev) => {
      const next = new Set(prev);
      let grew = false;
      ids.forEach((id) => { if (!next.has(id)) { next.add(id); grew = true; } });
      return grew ? next : prev;
    });
    if (!added) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { drain(); }, FLUSH_MS);
  }, [drain]);

  // 목록이 바뀌면 **처음부터 다시**. 자국까지 비우지 않으면 같은 pid 가 다른 목록에서
  // 영원히 빈 칸으로 남는다(구형이 4개 ref 를 비우던 자리와 같은 이유).
  const rowsKey = parsed.map((p) => p.problemId).join(',');
  useEffect(() => {
    askedRef.current = new Set();
    queueRef.current = new Set();
    setItems({});
    setHtmls({});
    setSeen(new Set());
  }, [rowsKey]);

  /**
   * 관찰 배선 — 컨테이너 안의 `[data-body-pid]` 를 전부 지켜본다.
   * root 를 스크롤 칸으로 준다. 안 주면 기준이 뷰포트라, rootMargin 400px 이 목록 칸이 아니라
   * 화면 기준으로 재져 안 보이는 카드까지 미리 받아 온다(useRowBodies 와 같은 이유).
   */
  useEffect(() => {
    const root = listRef.current;
    if (root == null || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      const hit = [];
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const pid = e.target.getAttribute('data-body-pid');
        if (pid) hit.push(pid);
      });
      if (hit.length > 0) enqueue(hit);
    }, { root: isDesktop ? root : null, rootMargin: ROOT_MARGIN, threshold: 0 });
    root.querySelectorAll('[data-body-pid]').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [rowsKey, enqueue, isDesktop]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  // 개념 사전 — 순번·유형은 개념 자체의 속성이라 문제 응답에 없다. 창당 한 번(모듈 캐시 5분).
  useEffect(() => {
    if (!showConcepts) return undefined;
    let alive = true;
    Promise.resolve(fetchConceptDictV2(getAccessToken))
      .then((d) => { if (alive) setDict(Array.isArray(d) ? d : null); })
      .catch(() => { if (alive) setDict(null); });
    return () => { alive = false; };
  }, [showConcepts, getAccessToken]);

  /**
   * 이 문제로 **AI 변형**을 만들러 간다 — 문제(/problem-hub)의 [변형] 탭(⑤).
   *
   * 구형 카드는 여기서 `/problem-agent` 로 나갔다(ProblemPreviewCards.js:531,
   * `createMode: 'prob_to_prob'`). 그 페이지는 v1 이라, 버튼만 v2 카드에 있고 **누르면 구형으로
   * 떨어지는** 상태였다 — 「모두 v2 로」가 이 한 자리에서 깨져 있었다(오너 지적 2026-08-30).
   *
   * v2 에서 그 기능의 이름은 「변형」이다(BE-20260825-ai-variation-screens.md §3) —
   * 원본 하나로 새 문제를 뽑는다는 점이 prob_to_prob 과 같다. 그래서 버튼 이름도 그쪽에 맞춘다.
   * 딥링크는 이미 있었고(`/problem-hub/problem/:problemId`), 탭은 주소(`?tab=variation`)로 정한다.
   * 종전에는 route state 로 넘겼는데, 주소에 남지 않아 뒤로가기로 돌아오면 탭이 사라졌다
   * (문제 상세의 탭이 주소 정본이 된 2026-09-11 에 함께 옮김).
   */
  const handleMakeVariation = useCallback((pid) => {
    navigate(`/problem-hub/problem/${pid}?tab=variation`);
  }, [navigate]);

  if (parsed.length === 0) {
    return <div className="px-4 py-6 text-center text-sm text-gray-400">문제 정보가 없습니다.</div>;
  }

  return (
    <div
      ref={listRef}
      /* ══ 가로 스크롤을 없앤다 — 세 가지가 겹쳐 있었다 ═══════════════════════
         ① `overflow-y-auto` 만 주면 **가로가 덩달아 auto 가 된다.** CSS 규칙상 한 축이
            `visible` 이 아니면 다른 축의 `visible` 은 `auto` 로 계산되기 때문이다.
            그래서 넘치는 것이 조금이라도 있으면 가로 막대가 뜬다. → `overflow-x-hidden` 을 못박는다.
         ② 개념 배지가 **줄지도 접히지도 않는다**(conceptBadge 의 `shrink-0 whitespace-nowrap`).
            `flex-wrap` 은 배지 *사이*만 줄을 바꾸지 배지 *자체*는 줄이지 않아, 이름이 긴 개념
            하나가 카드보다 넓으면 그대로 삐져나온다(예: 「삼각형의 모양(삼각형의 변과 각 사이의 관계)」).
            그 배지는 공용 부품이라 여기서 고치지 않고, 담는 쪽에서 `min-w-0` 으로 가둔다(아래).
         ③ 카드의 `hover:scale-[1.02]` 는 자리를 늘리지 않지만 **스크롤 영역에는 잡힌다.**

         ══ 세로 막대는 남기되 자리를 먹지 않게 한다 ══════════════════════════
         **`::-webkit-scrollbar` 를 건드리지 않는 것**이 답이다. 그 의사요소에 규칙을 하나라도
         주면 브라우저가 오버레이 막대를 포기하고 **자리를 차지하는 고전 막대**로 갈아탄다 —
         칸을 좁히던 6px 은 막대 자체가 아니라 그 커스텀이 부른 것이었다. 규칙을 걷어내면
         OS 기본 오버레이 막대로 돌아가, 스크롤할 때 콘텐츠 **위에 떠서** 보이고 폭은 그대로다.
         (반대로 `scrollbar-width:none` 으로 숨기면 스크롤은 되지만 막대가 사라진다 —
          「어디까지 왔는지」가 안 보이므로 쓰지 않는다.) */
      className={`${isDesktop ? 'h-full overflow-y-auto' : 'w-full'} overflow-x-hidden`}
    >
      <div className="min-h-full bg-white px-4 py-2">
        {parsed.map((p) => {
          const key = String(p.problemId);
          const item = items[key];
          // 난이도는 **v2 응답이 이긴다.** props 의 step/sequence[1] 은 호출부가 들고 있던
          // 옛 값일 수 있어, 같은 문제가 목록과 창에서 다른 Lv 로 보이던 원인이었다.
          const difficulty = item?.difficulty ?? p.difficulty;
          // conceptPairs 를 쓴다 — problemApi 의 normalizeConceptNames 는 **문자열 배열**을 주어
          // id 가 사라지고, 그러면 사전을 붙여도 순번·유형 칸이 영영 비어 있다.
          const concepts = showConcepts ? conceptPairs(item) : [];
          const html = htmls[key] ?? null;
          const isSeen = seen.has(key);

          return (
            <div
              key={key}
              data-body-pid={key}
              onClick={() => onHighlight && onHighlight(p.index, p.raw)}
              className={`relative mb-[14.4px] cursor-pointer rounded-3xl border bg-white p-[14.4px] shadow-lg transition-all duration-300 last:mb-0 hover:scale-[1.02] hover:border-primary/20 hover:shadow-2xl ${
                p.index === highlightedIndex ? 'border-transparent ring-2 ring-primary' : 'border-gray-200'
              }`}
            >
              {/* 카드 머리 — 번호·PID·난이도 / 버튼 둘 */}
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <ProblemNumber number={p.index + 1} isCorrect={p.isCorrect} />
                  <div className="flex items-center gap-2">
                    {/* 번호 — 두 메뉴가 함께 쓰는 정본 부품. 접두어 「PID: 」는 걷었다
                        (오너 결정 2026-09-01). 카드 전체는 지면의 그 문항을 짚는 일을 하고,
                        번호는 그 문제의 상세로 간다 — 하는 일이 달라 길이 둘이다. */}
                    <ProblemIdLink id={p.problemId} />
                    {/* 값이 없어도 칸을 세운다(구형과 같다) — 「Lv.-」가 자리를 지켜 행이 들썩이지 않는다 */}
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold text-white ${
                        difficultyColor(difficulty) ? '' : 'bg-gray-400'
                      }`}
                      style={difficultyColor(difficulty) ? { backgroundColor: difficultyColor(difficulty) } : undefined}
                    >
                      Lv.{difficulty || '-'}
                    </span>
                  </div>
                </div>

                <div className="flex gap-1">
                  {canRunAi && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleMakeVariation(p.problemId); }}
                      className="flex items-center gap-0.5 rounded-full bg-primary px-2.5 py-0.5 text-[10px] text-white transition-colors hover:bg-primary-hover"
                      title="이 문제로 AI 변형 만들기"
                    >
                      <Bot size={16} />
                    </button>
                  )}
                  {onViewProblem && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onViewProblem([p.problemId], p.index, p.isCorrect); }}
                      className="flex items-center gap-0.5 rounded-full bg-blue-500 px-2.5 py-0.5 text-[10px] text-white transition-colors hover:bg-blue-600"
                      title="문제 상세 보기"
                    >
                      <Eye size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* 수학 개념 — 배지 부품을 창(ProblemPreviewV2)과 나눠 쓴다.
                  구형은 같은 모양을 제 안에 한 벌 더 갖고 있었다(색 표까지 복사본). */}
              {showConcepts && concepts.length > 0 && (
                <div className="mb-2 rounded-lg bg-gray-50 p-2" onClick={(e) => e.stopPropagation()}>
                  <div className="mb-1 flex items-center gap-2">
                    <BookOpen aria-hidden="true" className="size-4 text-gray-600" />
                    <span className="text-xs font-medium text-gray-600">수학 개념</span>
                  </div>
                  {/* min-w-0 — 배지가 shrink-0 라 이 칸이 최소 내용 폭으로 벌어지는 것을 막는다.
                      긴 이름은 잘리지만 전체 이름은 배지의 title 로 남는다(마우스를 올리면 뜬다). */}
                  <div className="flex min-w-0 flex-wrap gap-1">
                    {concepts.map((c) => {
                      const m = matchConcept(dict, c.id);
                      return (
                        <ConceptBadge
                          key={`${c.id ?? 'x'}-${c.name}`}
                          name={c.name || m?.concept_name || m?.text || ''}
                          order={m?.order ?? null}
                          type={m?.concept_type ?? null}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 본문 — 창과 **같은 렌더러**다. 배율 상한만 목록 값으로 내려 옆 칸 글자와 크기를
                  맞춘다(18 × 0.67 ≈ 12px — 구형 카드가 !important 로 강제하던 그 값이다). */}
              {!isSeen ? (
                // 아직 눈에 안 든 카드 — 자리만 잡아 둔다. 높이를 안 주면 관찰이 한꺼번에 발화해
                // 지연 로딩이 무의미해진다.
                <div className="mt-2 h-24 rounded-2xl bg-gray-50" />
              ) : item === undefined ? (
                <div className="mt-2 flex h-24 items-center justify-center rounded-2xl bg-gray-50">
                  <span className="size-6 animate-spin rounded-full border-b-2 border-gray-600" />
                </div>
              ) : (
                <ProblemBodyPreview
                  html={html}
                  fallback={html == null ? (item?.preview || '본문 미리보기 없음') : null}
                  maxScale={LIST_BODY_MAX_SCALE}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ProblemCardsV2;
